import axios from 'axios';
import dbUrl from '../utils/credentials';

export function normalizeLegacyResponses(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((r, index) => ({
      id: `legacy_${index}`,
      ...r,
      _legacy: true,
      _legacyIndex: index,
    }));
  }
  if (typeof raw === 'object') {
    if (raw.answers) {
      return [{ id: 'legacy_0', ...raw, _legacy: true, _legacyIndex: 0 }];
    }
    return Object.entries(raw).map(([id, r]) => ({
      id: String(id),
      ...r,
      _legacy: !String(id).startsWith('r_'),
    }));
  }
  return [];
}

export async function fetchInvitationResponses(invitationId, legacyEmbedded) {
  const legacy = normalizeLegacyResponses(legacyEmbedded);
  try {
    const res = await axios.get(`${dbUrl}invitations/${invitationId}/responses.json`);
    const data = res.data;
    if (!data) return legacy;

    let fromSubnode = [];
    if (Array.isArray(data)) {
      fromSubnode = data.map((r, i) => ({ id: `r_${i}`, ...r }));
    } else if (typeof data === 'object') {
      fromSubnode = Object.entries(data).map(([id, r]) => ({ id, ...r }));
    }

    if (fromSubnode.length > 0) return fromSubnode;
    return legacy;
  } catch {
    return legacy;
  }
}

export function stripResponsesFromInvitation(invitation) {
  if (!invitation) return invitation;
  const { responses, ...rest } = invitation;
  return rest;
}

export async function submitResponse(invitationId, answers) {
  const responseId = `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const payload = {
    submittedAt: new Date().toISOString(),
    answers,
  };
  await axios.put(`${dbUrl}invitations/${invitationId}/responses/${responseId}.json`, payload);
  return { id: responseId, ...payload };
}

export async function updateResponse(invitationId, response, allResponses, _invitation) {
  if (response._legacy) {
    const updated = allResponses.map((r) => (r.id === response.id ? response : r));
    const legacyOnly = updated.filter((r) => r._legacy).map(({ _legacy, _legacyIndex, id, ...rest }) => rest);
    // PATCH only the responses key: a full PUT would wipe sibling data under the invitation.
    await axios.patch(`${dbUrl}invitations/${invitationId}.json`, {
      responses: legacyOnly,
    });
    return updated;
  }

  const { id, _legacy, _legacyIndex, ...payload } = response;
  await axios.put(`${dbUrl}invitations/${invitationId}/responses/${id}.json`, payload);
  return allResponses.map((r) => (r.id === id ? response : r));
}

export async function deleteResponse(invitationId, responseId, allResponses, _invitation) {
  const target = allResponses.find((r) => r.id === responseId);
  if (target?._legacy) {
    const updated = allResponses.filter((r) => r.id !== responseId);
    const legacyOnly = updated.filter((r) => r._legacy).map(({ _legacy, _legacyIndex, id, ...rest }) => rest);
    // PATCH only the responses key: a full PUT would wipe sibling data under the invitation.
    await axios.patch(`${dbUrl}invitations/${invitationId}.json`, {
      responses: legacyOnly,
    });
    return updated;
  }

  await axios.delete(`${dbUrl}invitations/${invitationId}/responses/${responseId}.json`);
  return allResponses.filter((r) => r.id !== responseId);
}

export function countOptionUsage(responses, fieldName, optionLabel) {
  return responses.reduce((count, response) => {
    const answers = response.answers ? Object.values(response.answers) : [];
    const entry = answers.find((a) => a.label === fieldName);
    if (!entry) return count;
    const val = entry.value;
    if (Array.isArray(val)) return val.includes(optionLabel) ? count + 1 : count;
    return val === optionLabel ? count + 1 : count;
  }, 0);
}

export async function getResponseCount(invitationId, legacyEmbedded) {
  const legacyCount = normalizeLegacyResponses(legacyEmbedded).length;
  try {
    const res = await axios.get(`${dbUrl}invitations/${invitationId}/responses.json`);
    if (!res.data) return legacyCount;
    const subnodeCount = Array.isArray(res.data)
      ? res.data.length
      : Object.keys(res.data).length;
    if (subnodeCount > 0) return subnodeCount;
    return legacyCount;
  } catch {
    return legacyCount;
  }
}

export function getFieldStats(responses, field) {
  if (field.type !== 'select') return null;
  const options = field.options || [];
  return options.map((opt) => {
    const label = typeof opt === 'string' ? opt : opt.label;
    const maxCount = typeof opt === 'object' ? opt.maxCount : null;
    const count = countOptionUsage(responses, field.name, label);
    return { label, count, maxCount, full: maxCount != null && count >= maxCount };
  });
}
